/**
 * Reads the `alarms_live_*` parquet family for the Alarms explore pages.
 *
 * WHY THIS IS NOT unifiedDataStore. `fetchAndStoreTimeSeriesData` builds the channel-values payload
 * for TELEMETRY: it injects `ts`, merges metadata and filter channels, and applies a resolution.
 * Alarms are discrete records in their own parquet family, so this posts channel-values directly
 * with `parquet_basename_prefix: 'alarms_'`, which makes the server read that family and ONLY that
 * family (no Influx tiers, no telemetry files) and forces the resolution to null.
 *
 * The column list mirrors `shared/utils/alarmStreams.js` ALARM_COLUMNS. There is deliberately no
 * `Datetime` column in that family — `ts` (epoch SECONDS) is canonical and the page derives the
 * display time from it.
 */

import { apiEndpoints } from '@config/env';
import { postBinary, getData, getTimezoneForDate } from '@utils/global';
import { runWithConcurrency, FLEET_SOURCE_FETCH_CONCURRENCY } from '@utils/concurrency';
import { warn } from '@utils/console';

/** Basename prefix that selects the alarms family. Must match liveAppend.js FAMILIES.alarms. */
export const ALARMS_PARQUET_PREFIX = 'alarms_';

/**
 * Calendar day as YYYYMMDD.
 *
 * `datasets.date` arrives as `YYYY-MM-DD` or as an ISO timestamp (`2026-09-20T00:00:00.000Z`).
 * The file server uses the string as a folder name, so anything past the day would look in a
 * folder that does not exist. The first eight digits after stripping separators are the day
 * written in the string — no timezone shift.
 */
export function compactAlarmDateYmd(raw: string | null | undefined): string {
  const compact = String(raw ?? '').trim().replace(/[-/]/g, '').slice(0, 8);
  return /^\d{8}$/.test(compact) ? compact : '';
}

/**
 * Folder date for an alarms read.
 *
 * Live keeps today's UTC day when nothing is selected: that is the folder alarmPersister writes.
 * A selected day (the fleet day page) wins. A dataset explore clears `selectedDate`, so the
 * dataset's own date is the folder — falling through to today reads an empty path.
 */
export function resolveAlarmQueryDate(args: {
  live: boolean;
  selectedDate?: string | null;
  datasetDate?: string | null;
  todayUtcYmd: string;
}): string {
  const selected = compactAlarmDateYmd(args.selectedDate);
  if (args.live) {return selected || compactAlarmDateYmd(args.todayUtcYmd);}
  if (selected) {return selected;}
  return compactAlarmDateYmd(args.datasetDate);
}

/** One alarm as the pages consume it. `ts` is epoch seconds. */
export interface AlarmRow {
  ts: number;
  Source: string;
  Category: string;
  Description: string;
  Channel: string;
  Value: number | null;
}

/** Channel list for the request — kept in step with ALARM_COLUMNS. */
const ALARM_CHANNEL_LIST = [
  { name: 'ts', type: 'float' },
  { name: 'Source', type: 'string' },
  { name: 'Category', type: 'string' },
  { name: 'Description', type: 'string' },
  { name: 'Channel', type: 'string' },
  { name: 'Value', type: 'float' },
];

export interface FetchAlarmsArgs {
  className: string;
  projectId: number;
  /** YYYYMMDD. */
  date: string;
  sourceName: string;
  timezone?: string;
  signal?: AbortSignal;
}

function str(row: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = row[k] ?? row[k.toLowerCase()] ?? row[k.toUpperCase()];
    if (v !== undefined && v !== null && String(v).trim() !== '') {return String(v).trim();}
  }
  return '';
}

function num(row: Record<string, unknown>, ...keys: string[]): number | null {
  for (const k of keys) {
    const v = row[k] ?? row[k.toLowerCase()] ?? row[k.toUpperCase()];
    if (v === undefined || v === null || v === '') {continue;}
    const n = typeof v === 'bigint' ? Number(v) : Number(v);
    if (Number.isFinite(n)) {return n;}
  }
  return null;
}

/** Arrow column casing varies, so read every field case-insensitively. */
function normalizeRow(raw: unknown, fallbackSource: string): AlarmRow | null {
  if (raw == null || typeof raw !== 'object') {return null;}
  const row = raw as Record<string, unknown>;
  const ts = num(row, 'ts');
  if (ts === null) {return null;}
  return {
    ts,
    // A row written before Source existed, or a projection that dropped it, still belongs to the
    // boat whose folder it came from.
    Source: str(row, 'Source') || fallbackSource,
    Category: str(row, 'Category'),
    Description: str(row, 'Description'),
    Channel: str(row, 'Channel'),
    Value: num(row, 'Value'),
  };
}

/**
 * Collapse rows that are the same alarm read from two files.
 *
 * A date can hold BOTH `alarms_live_*.parquet` (written as the day streamed) and
 * `alarms_<date>.parquet` (written later by scripts/backfill_alarms.cjs). The read selects the
 * family by the `alarms_` prefix, so it unions them, and the same physical alarm appears twice.
 *
 * Keyed on the whole second rather than the raw `ts`: the two writers cannot agree below that.
 * The live run evaluated the rules on the feed as it arrived; the backfill replays the same rules
 * over the same Influx samples later, and `alarmMonitor` additionally nudges simultaneous alarms
 * by a millisecond, so identical `ts` never happens across writers.
 *
 * LIMIT, stated rather than hidden: this catches duplicates landing in the same second, which is
 * the common case. A crossing that the two passes resolve a second or more apart will still show
 * twice. Eliminating that entirely would mean not backfilling days that already ran live.
 */
/** Stable row identity for dedupe and solid-js/store reconcile (same second + fields). */
export function alarmRowKey(r: AlarmRow): string {
  return `${Math.floor(r.ts)}|${r.Source}|${r.Category}|${r.Description}`;
}

export function dedupeAlarmRows(rows: AlarmRow[]): AlarmRow[] {
  const seen = new Set<string>();
  const out: AlarmRow[] = [];
  for (const r of rows) {
    const key = alarmRowKey(r);
    if (seen.has(key)) {continue;}
    seen.add(key);
    out.push(r);
  }
  return out;
}

/**
 * Alarms for ONE source on one date.
 * A 404 means no alarms parquet exists for that boat that day, which is normal — returns [].
 */
export async function fetchAlarmsForSource(args: FetchAlarmsArgs): Promise<AlarmRow[]> {
  const { className, projectId, date, sourceName, timezone, signal } = args;
  const res = await postBinary(
    apiEndpoints.file.channelValues,
    {
      project_id: projectId,
      class_name: className,
      date,
      source_name: sourceName,
      channel_list: ALARM_CHANNEL_LIST,
      start_ts: null,
      end_ts: null,
      resolution: null,
      data_source: 'file',
      timezone: timezone ?? 'UTC',
      parquet_basename_prefix: ALARMS_PARQUET_PREFIX,
    },
    signal
  );

  if (!res.success) {
    // 404 = no matching parquet for this source/date. Anything else is worth surfacing, but one
    // boat failing must not empty the whole fleet table.
    //
    // An abort is not a failure either, and it does not look like one: `postBinary` catches the
    // AbortError in its network-error branch, which returns no `status` at all, so it used to slip
    // past the 404 test and log. One context change produced FLEET_SOURCE_FETCH_CONCURRENCY of
    // these at once - a burst that read as a server fault when it was just the caller moving on.
    if (signal?.aborted) {return [];}
    if (res.status !== 404) {
      warn('[alarms] channel-values failed', { sourceName, date, status: res.status });
    }
    return [];
  }

  const rows = Array.isArray(res.data) ? res.data : [];
  const out: AlarmRow[] = [];
  for (const r of rows) {
    const row = normalizeRow(r, sourceName);
    if (row) {out.push(row);}
  }
  // A source's live file and its backfill file are both in this response - see dedupeAlarmRows.
  return dedupeAlarmRows(out.sort((a, b) => a.ts - b.ts));
}

export interface DateSource {
  source_id: number;
  source_name: string;
  dataset_id: number;
}

/**
 * The boats that actually have a dataset on this date.
 * `sourcesStore` is the project-wide catalogue (every boat ever), which is not the same thing.
 */
export async function fetchSourcesForDate(
  className: string,
  projectId: number,
  dateYmd: string,
  timezone?: string
): Promise<DateSource[]> {
  const dateStr =
    dateYmd.length === 8 ? `${dateYmd.slice(0, 4)}-${dateYmd.slice(4, 6)}-${dateYmd.slice(6, 8)}` : dateYmd;
  let url =
    `${apiEndpoints.app.datasets}/date/dataset_id` +
    `?class_name=${encodeURIComponent(className)}` +
    `&project_id=${encodeURIComponent(String(projectId))}` +
    `&date=${encodeURIComponent(dateStr)}`;
  if (timezone) {url += `&timezone=${encodeURIComponent(timezone)}`;}

  const resp = await getData(url);
  const list = (resp && (resp as { data?: unknown }).data) ?? (Array.isArray(resp) ? resp : []);
  if (!Array.isArray(list)) {return [];}

  const seen = new Set<string>();
  const out: DateSource[] = [];
  for (const raw of list) {
    const row = raw as Record<string, unknown>;
    const name = String(row.source_name ?? row.sourceName ?? '').trim();
    if (!name || seen.has(name.toUpperCase())) {continue;}
    seen.add(name.toUpperCase());
    out.push({
      source_id: Number(row.source_id ?? row.sourceId ?? 0),
      source_name: name,
      dataset_id: Number(row.dataset_id ?? row.datasetId ?? 0),
    });
  }
  return out;
}

/**
 * Alarms for EVERY boat on a date, merged and sorted by time.
 *
 * Fanned out at FLEET_SOURCE_FETCH_CONCURRENCY: the alarms family is one parquet per boat, and
 * firing them all at once oversubscribes the file server's DuckDB pool so the losers time out and
 * that boat silently vanishes from the table. A single boat's failure yields [] for that boat only.
 */
export async function fetchFleetAlarms(
  className: string,
  projectId: number,
  dateYmd: string,
  signal?: AbortSignal
): Promise<AlarmRow[]> {
  const timezone = (await getTimezoneForDate(className, projectId, dateYmd).catch(() => null)) || 'UTC';
  const sources = await fetchSourcesForDate(className, projectId, dateYmd, timezone);
  if (sources.length === 0) {return [];}

  const tasks = sources.map((s) => async () => {
    if (signal?.aborted) {return [] as AlarmRow[];}
    try {
      return await fetchAlarmsForSource({
        className,
        projectId,
        date: dateYmd,
        sourceName: s.source_name,
        timezone,
        signal,
      });
    } catch (err) {
      warn('[alarms] fleet source failed', { source: s.source_name, err: (err as Error)?.message });
      return [] as AlarmRow[];
    }
  });

  const perSource = await runWithConcurrency(tasks, FLEET_SOURCE_FETCH_CONCURRENCY);
  // Each task already deduped its own source; the key includes Source, so a second pass over the
  // merged fleet list is harmless and guards against a boat appearing under two dataset rows.
  return dedupeAlarmRows(perSource.flat().sort((a, b) => a.ts - b.ts));
}
